
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSettings } from "@/contexts/SettingsContext";
import { useI18n } from "@/i18n/LanguageContext";

interface CountdownTimerProps {
  deadline?: string;
  /** Visual density; `compact` is used inside the hero card. */
  size?: 'default' | 'compact';
}

const FALLBACK_DEADLINE = '2025-07-15T22:00:00';

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ deadline: propDeadline, size = 'default' }) => {
  const { getSetting } = useSettings();
  const { t } = useI18n();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Get deadline from global settings or prop
  const deadline = propDeadline || getSetting('admission_deadline') || FALLBACK_DEADLINE;

  const deadlineDate = useMemo(() => {
    const parsed = new Date(deadline);
    return Number.isNaN(parsed.getTime()) ? new Date(FALLBACK_DEADLINE) : parsed;
  }, [deadline]);

  const calculateTimeLeft = useCallback(() => {
    const difference = deadlineDate.getTime() - Date.now();
    if (difference > 0) {
      setTimeLeft({
        days: Math.floor(difference / 86400000),
        hours: Math.floor((difference / 3600000) % 24),
        minutes: Math.floor((difference / 60000) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      });
    } else {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }
  }, [deadlineDate]);

  useEffect(() => {
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const cells = [
    { value: timeLeft.days, label: t.countdown.days },
    { value: timeLeft.hours, label: t.countdown.hours },
    { value: timeLeft.minutes, label: t.countdown.minutes },
    { value: timeLeft.seconds, label: t.countdown.seconds },
  ];

  const compact = size === 'compact';

  return (
    <div className={compact ? "mt-3 grid grid-cols-4 gap-2" : "mt-4 grid grid-cols-4 gap-2 sm:gap-4"} role="timer" aria-live="off">
      {cells.map((cell) => (
        <div
          key={cell.label}
          className={
            compact
              ? "rounded-md border border-primary/25 bg-background/70 px-1 py-2 text-center"
              : "rounded-lg border border-primary/30 bg-background/60 p-2 text-center backdrop-blur-sm sm:p-3"
          }
        >
          <div className={compact ? "font-display text-xl font-bold tabular-nums text-foreground" : "font-display text-2xl font-bold tabular-nums text-foreground sm:text-3xl"}>
            {cell.value.toString().padStart(2, '0')}
          </div>
          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-xs">{cell.label}</div>
        </div>
      ))}
    </div>
  );
};
